import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import {
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  useTableSort,
} from "@/components/dataTable";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SelectInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  buildEmployeeHoursDetail,
  buildProjectOpenHoursDetail,
  fetchTimesheetsByAuftrag,
  formatDateDe,
  formatDurationHours,
  getStatusInfo,
  markHeroEntriesStatus,
  rowSecondsBreakdown,
  type EmployeeHoursEntry,
  type HeroHourMarkStatus,
  type HeroUsageRow,
  type OpenHoursDetailSection,
  type ProjectOpenHoursEntry,
  type TimesheetByAuftragOption,
} from "@/lib/auswertungen";
import { timesheetStatusLabel } from "@/pages/stundennachweise/timesheetFilterLogic";

const MARK_STATUS_LABELS: Record<HeroHourMarkStatus, string> = {
  festpreis: "Festpreis",
  auf_nachweis: "Auf Nachweis",
  ohne_berechnung: "Ohne Berechnung",
  gewaehrleistung: "Gewährleistung",
};

function EntryTable({
  entries,
  openHoursOnly = false,
}: {
  entries: HeroUsageRow[];
  openHoursOnly?: boolean;
}) {
  const { sort, toggle, reset } = useTableSort();
  const sortedEntries = useMemo(
    () =>
      sortRowsByKey(entries, sort, (row, key) => {
        if (key === "date") return row.work_date;
        if (key === "duration") {
          return openHoursOnly
            ? rowSecondsBreakdown(row).unassignedSeconds
            : Number(row.duration_in_seconds ?? row.duration ?? 0);
        }
        if (key === "status") return getStatusInfo(row).status;
        return row.comment;
      }),
    [entries, openHoursOnly, sort],
  );
  if (entries.length === 0) {
    return <p className="text-sm text-label-secondary">Keine Einträge.</p>;
  }

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={reset} />
      <div className="overflow-x-auto rounded-ios border border-separator">
      <table className="eh-data-table min-w-full divide-y divide-separator text-sm">
        <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
          <tr>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Datum" columnKey="date" sort={sort} onSort={toggle} /></th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Dauer" columnKey="duration" sort={sort} onSort={toggle} /></th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Status" columnKey="status" sort={sort} onSort={toggle} /></th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Kommentar" columnKey="comment" sort={sort} onSort={toggle} /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-separator bg-card">
          {sortedEntries.map((row, index) => {
            const seconds = openHoursOnly
              ? rowSecondsBreakdown(row).unassignedSeconds
              : Number(row.duration_in_seconds ?? row.duration ?? 0);
            const status = getStatusInfo(row);
            return (
              <tr
                key={`${row.hero_uuid ?? "row"}:${index}`}
                className={`eh-data-row eh-report-row--${status.classState}`}
              >
                <td className="eh-data-body-cell whitespace-nowrap px-3 py-2">
                  {formatDateDe(row.work_date)}
                </td>
                <td className="eh-data-body-cell whitespace-nowrap px-3 py-2 tabular-nums">
                  {formatDurationHours(seconds)}
                </td>
                <td className="eh-data-body-cell px-3 py-2">{status.status}</td>
                <td className="eh-data-body-cell px-3 py-2">{row.comment ?? ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function selectableEntries(entries: HeroUsageRow[]): HeroUsageRow[] {
  return entries.filter((entry) => Boolean(String(entry.hero_uuid || "").trim()));
}

function SelectableEmployeeEntryTable({
  sectionKey,
  entries,
  selectedUuids,
  onToggleUuids,
}: {
  sectionKey: string;
  entries: HeroUsageRow[];
  selectedUuids: Set<string>;
  onToggleUuids: (uuids: string[], selected: boolean) => void;
}) {
  const { sort, toggle, reset } = useTableSort();
  const sortedEntries = useMemo(
    () =>
      sortRowsByKey(entries, sort, (row, key) => {
        if (key === "date") return row.work_date;
        if (key === "duration") return Number(row.duration_in_seconds ?? row.duration ?? 0);
        if (key === "status") return getStatusInfo(row).status;
        return row.comment;
      }),
    [entries, sort],
  );
  if (entries.length === 0) {
    return <p className="text-sm text-label-secondary">Keine Einträge.</p>;
  }

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={reset} />
      <div className="overflow-x-auto rounded-ios border border-separator">
      <table className="eh-data-table min-w-full divide-y divide-separator text-sm">
        <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
          <tr>
            <th className="eh-data-th w-10 px-3 py-2 text-left font-medium">Ausw.</th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Datum" columnKey="date" sort={sort} onSort={toggle} /></th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Dauer" columnKey="duration" sort={sort} onSort={toggle} /></th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Status" columnKey="status" sort={sort} onSort={toggle} /></th>
            <th className="eh-data-th px-3 py-2 text-left font-medium"><SortableHeader label="Kommentar" columnKey="comment" sort={sort} onSort={toggle} /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-separator bg-card">
          {sortedEntries.map((row, index) => {
            const seconds = Number(row.duration_in_seconds ?? row.duration ?? 0);
            const status = getStatusInfo(row);
            const rowUuid = String(row.hero_uuid || "");
            const canMark = Boolean(rowUuid);

            return (
              <tr
                key={`${sectionKey}:${rowUuid}:${index}`}
                className={`eh-data-row eh-report-row--${status.classState}`}
              >
                <td className="eh-data-body-cell px-3 py-2 align-top">
                  {canMark ? (
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                      checked={selectedUuids.has(rowUuid)}
                      aria-label={`Eintrag ${formatDateDe(row.work_date)} auswählen`}
                      onChange={(event) => onToggleUuids([rowUuid], event.target.checked)}
                    />
                  ) : (
                    <span className="inline-block h-4 w-4" aria-hidden />
                  )}
                </td>
                <td className="eh-data-body-cell whitespace-nowrap px-3 py-2">
                  {formatDateDe(row.work_date)}
                </td>
                <td className="eh-data-body-cell whitespace-nowrap px-3 py-2 tabular-nums">
                  {formatDurationHours(seconds)}
                </td>
                <td className="eh-data-body-cell px-3 py-2">{status.status}</td>
                <td className="eh-data-body-cell px-3 py-2">{row.comment ?? ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function EmployeeStickyActionBar({
  totals,
  selectedCount,
  marking,
  disabled,
  aufNachweisEnabled,
  aufNachweisHint,
  onMarkStatus,
  onOpenAufNachweis,
}: {
  totals: EmployeeHoursEntry;
  selectedCount: number;
  marking: boolean;
  disabled: boolean;
  aufNachweisEnabled: boolean;
  aufNachweisHint: string | null;
  onMarkStatus: (status: HeroHourMarkStatus) => void;
  onOpenAufNachweis: () => void;
}) {
  return (
    <div className="shrink-0 border-b border-separator bg-card px-5 py-3 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-wrap gap-x-4 gap-y-1 text-sm text-label-secondary">
          <span className="font-medium text-label">Summen gesamt</span>
          <span>Ohne Projekt: {formatDurationHours(totals.unmappedSeconds)}</span>
          <span>Ohne Nachweis: {formatDurationHours(totals.unassignedSeconds)}</span>
          <span>Mit Nachweis: {formatDurationHours(totals.withProofSeconds)}</span>
          <span>Auf Nachweis: {formatDurationHours(totals.aufNachweisSeconds)}</span>
          <span>Festpreis: {formatDurationHours(totals.festpreisSeconds)}</span>
          <span>Ohne Berechnung: {formatDurationHours(totals.ohneBerechnungSeconds)}</span>
          <span>Gewährleistung: {formatDurationHours(totals.gewaehrleistungSeconds)}</span>
          <span className="font-medium text-label">
            Gesamt: {formatDurationHours(totals.totalSeconds)}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="text-sm text-label-secondary">
              {selectedCount > 0
                ? `${selectedCount} Zeiteinträge ausgewählt`
                : "Keine Zeiteinträge ausgewählt"}
            </span>
            <Button
              type="button"
              size="sm"
              disabled={disabled || selectedCount === 0 || marking}
              loading={marking}
              onClick={() => onMarkStatus("festpreis")}
            >
              Auf Festpreis setzen (FP)
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={!aufNachweisEnabled || marking}
              title={aufNachweisHint ?? undefined}
              onClick={onOpenAufNachweis}
            >
              Stunden auf Nachweis
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={disabled || selectedCount === 0 || marking}
              onClick={() => onMarkStatus("ohne_berechnung")}
            >
              Ohne Berechnung
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={disabled || selectedCount === 0 || marking}
              onClick={() => onMarkStatus("gewaehrleistung")}
            >
              Gewährleistung
            </Button>
          </div>
          {aufNachweisHint && selectedCount > 0 && !aufNachweisEnabled ? (
            <p className="text-xs text-label-secondary">{aufNachweisHint}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SectionBreakdown({
  section,
}: {
  section: {
    unmappedSeconds: number;
    unassignedSeconds: number;
    withProofSeconds: number;
    festpreisSeconds: number;
    aufNachweisSeconds: number;
    ohneBerechnungSeconds: number;
    gewaehrleistungSeconds: number;
  };
}) {
  const parts: string[] = [];
  if (section.unmappedSeconds > 0) {
    parts.push(`Ohne Projekt ${formatDurationHours(section.unmappedSeconds)}`);
  }
  if (section.unassignedSeconds > 0) {
    parts.push(`Ohne Nachweis ${formatDurationHours(section.unassignedSeconds)}`);
  }
  if (section.withProofSeconds > 0) {
    parts.push(`Mit Nachweis ${formatDurationHours(section.withProofSeconds)}`);
  }
  if (section.aufNachweisSeconds > 0) {
    parts.push(`Auf Nachweis ${formatDurationHours(section.aufNachweisSeconds)}`);
  }
  if (section.festpreisSeconds > 0) {
    parts.push(`Festpreis ${formatDurationHours(section.festpreisSeconds)}`);
  }
  if (section.ohneBerechnungSeconds > 0) {
    parts.push(`Ohne Berechnung ${formatDurationHours(section.ohneBerechnungSeconds)}`);
  }
  if (section.gewaehrleistungSeconds > 0) {
    parts.push(`Gewährleistung ${formatDurationHours(section.gewaehrleistungSeconds)}`);
  }
  if (parts.length === 0) return null;
  return <p className="mt-0.5 text-xs text-label-secondary">{parts.join(" · ")}</p>;
}

function EmployeeProjectSection({
  section,
  sectionKey,
  selectedUuids,
  onToggleUuids,
}: {
  section: OpenHoursDetailSection;
  sectionKey: string;
  selectedUuids: Set<string>;
  onToggleUuids: (uuids: string[], selected: boolean) => void;
}) {
  const markableUuids = useMemo(
    () =>
      selectableEntries(section.entries)
        .map((entry) => String(entry.hero_uuid))
        .filter(Boolean),
    [section.entries],
  );
  const allSelected =
    markableUuids.length > 0 && markableUuids.every((uuid) => selectedUuids.has(uuid));
  const someSelected =
    markableUuids.some((uuid) => selectedUuids.has(uuid)) && !allSelected;

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            {markableUuids.length > 0 ? (
              <label className="flex items-center gap-2 text-sm text-label">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                  checked={allSelected}
                  ref={(element) => {
                    if (element) element.indeterminate = someSelected;
                  }}
                  aria-label={`Alle Einträge für ${section.label} auswählen`}
                  onChange={(event) => onToggleUuids(markableUuids, event.target.checked)}
                />
                <span className="text-xs text-label-secondary">Alle Einträge</span>
              </label>
            ) : null}
            <ProjectOverviewLink
              auftragId={section.entries[0]?.auftrag_id}
              label={section.label}
              labelClassName="text-sm font-semibold text-tint"
            />
          </div>
          <SectionBreakdown section={section} />
        </div>
        <span className="text-sm font-medium tabular-nums text-label">
          {formatDurationHours(section.totalSeconds)}
        </span>
      </div>
      <SelectableEmployeeEntryTable
        sectionKey={sectionKey}
        entries={section.entries}
        selectedUuids={selectedUuids}
        onToggleUuids={onToggleUuids}
      />
    </section>
  );
}

export function OpenHoursRankingDetailDialog({
  open,
  onClose,
  mode,
  project,
  partnerName,
  rows,
  onEntriesUpdated,
}: {
  open: boolean;
  onClose: () => void;
  mode: "project" | "employee" | null;
  project: ProjectOpenHoursEntry | null;
  partnerName: string | null;
  rows: HeroUsageRow[];
  onEntriesUpdated?: () => void | Promise<void>;
}) {
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(() => new Set());
  const [marking, setMarking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [timesheetPickerOpen, setTimesheetPickerOpen] = useState(false);
  const [timesheetOptions, setTimesheetOptions] = useState<TimesheetByAuftragOption[]>([]);
  const [timesheetOptionsLoading, setTimesheetOptionsLoading] = useState(false);
  const [timesheetOptionsError, setTimesheetOptionsError] = useState<string | null>(null);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<number | null>(null);

  const projectDetail = useMemo(
    () => (project ? buildProjectOpenHoursDetail(rows, project) : null),
    [project, rows],
  );

  const employeeDetail = useMemo(
    () => (partnerName ? buildEmployeeHoursDetail(rows, partnerName) : null),
    [partnerName, rows],
  );

  useEffect(() => {
    if (!open || mode !== "employee") {
      setSelectedUuids(new Set());
      setActionError(null);
      setActionMessage(null);
      setTimesheetPickerOpen(false);
      setTimesheetOptions([]);
      setSelectedTimesheetId(null);
      setTimesheetOptionsError(null);
    }
  }, [open, mode, partnerName]);

  const toggleUuids = useCallback((uuids: string[], selected: boolean) => {
    setSelectedUuids((current) => {
      const next = new Set(current);
      for (const uuid of uuids) {
        if (selected) next.add(uuid);
        else next.delete(uuid);
      }
      return next;
    });
    setActionError(null);
    setActionMessage(null);
  }, []);

  const selectedEntries = useMemo(() => {
    if (!employeeDetail || selectedUuids.size === 0) return [];
    const entries: HeroUsageRow[] = [];
    for (const section of employeeDetail.sections) {
      for (const entry of section.entries) {
        const uuid = String(entry.hero_uuid || "");
        if (uuid && selectedUuids.has(uuid)) entries.push(entry);
      }
    }
    return entries;
  }, [employeeDetail, selectedUuids]);

  const selectedAuftragId = useMemo(() => {
    if (selectedEntries.length === 0) return null;
    const ids = new Set<number>();
    for (const entry of selectedEntries) {
      if (entry.auftrag_id == null) return null;
      const id = Number(entry.auftrag_id);
      if (!Number.isFinite(id)) return null;
      ids.add(id);
    }
    if (ids.size !== 1) return null;
    return [...ids][0] ?? null;
  }, [selectedEntries]);

  const aufNachweisEnabled = selectedEntries.length > 0 && selectedAuftragId != null;
  const aufNachweisHint =
    selectedEntries.length === 0
      ? null
      : selectedAuftragId == null
        ? "Nur Stunden aus einem Projekt auswählen (mit Projektzuordnung)."
        : null;

  const handleMarkStatus = useCallback(
    async (status: HeroHourMarkStatus, timesheetId?: number | null) => {
      if (selectedEntries.length === 0) return;
      if (status === "auf_nachweis" && timesheetId == null) return;
      setMarking(true);
      setActionError(null);
      setActionMessage(null);
      const label = MARK_STATUS_LABELS[status];
      try {
        const result = await markHeroEntriesStatus(
          status,
          selectedEntries.map((entry) => ({
            hero_uuid: String(entry.hero_uuid),
            comment: entry.comment,
          })),
          timesheetId,
        );
        const parts: string[] = [];
        if (result.updated.length > 0) {
          parts.push(`${result.updated.length} Eintrag/Einträge als ${label} markiert`);
        }
        if (result.skipped.length > 0) {
          parts.push(`${result.skipped.length} übersprungen`);
        }
        if (result.failed.length > 0) {
          parts.push(`${result.failed.length} fehlgeschlagen`);
        }
        if (result.updated.length === 0 && result.failed.length > 0) {
          const firstError = result.failed[0]?.error || "Unbekannter Fehler";
          setActionError(firstError);
        } else if (parts.length > 0) {
          setActionMessage(parts.join(" · "));
        }
        if (result.updated.length > 0) {
          setSelectedUuids(new Set());
          setTimesheetPickerOpen(false);
          setSelectedTimesheetId(null);
          await onEntriesUpdated?.();
        }
      } catch (err) {
        setActionError(
          err instanceof ApiError ? err.message : "Status-Markierung fehlgeschlagen.",
        );
      } finally {
        setMarking(false);
      }
    },
    [onEntriesUpdated, selectedEntries],
  );

  const openTimesheetPicker = useCallback(async () => {
    if (selectedAuftragId == null) return;
    setTimesheetPickerOpen(true);
    setTimesheetOptionsLoading(true);
    setTimesheetOptionsError(null);
    setSelectedTimesheetId(null);
    try {
      const options = await fetchTimesheetsByAuftrag(selectedAuftragId);
      setTimesheetOptions(options);
      const only = options.length === 1 ? options[0] : undefined;
      if (only) {
        setSelectedTimesheetId(only.id);
      }
    } catch (err) {
      setTimesheetOptions([]);
      setTimesheetOptionsError(
        err instanceof ApiError
          ? err.message
          : "Stundennachweise konnten nicht geladen werden.",
      );
    } finally {
      setTimesheetOptionsLoading(false);
    }
  }, [selectedAuftragId]);

  const title =
    mode === "project" && project
      ? project.projekt
      : mode === "employee" && partnerName
        ? partnerName
        : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="4xl"
      panelClassName={
        mode === "employee" ? "max-h-[min(920px,calc(100dvh-2rem))]" : undefined
      }
      bodyClassName={
        mode === "employee" ? "flex min-h-0 flex-1 flex-col overflow-hidden p-0" : undefined
      }
    >
      {mode === "project" && projectDetail ? (
        <div className="space-y-5">
          {project?.auftragId ? (
            <ProjectOverviewLink
              auftragId={project.auftragId}
              label={project.projekt}
              labelClassName="text-sm font-semibold text-tint"
            />
          ) : null}
          <p className="text-sm text-label-secondary">
            Offene Stunden ohne Stundennachweis:{" "}
            <span className="font-medium text-status-red">
              {formatDurationHours(projectDetail.totalSeconds)}
            </span>
          </p>

          {projectDetail.sections.length === 0 ? (
            <p className="text-sm text-label-secondary">Keine offenen Stundeneinträge.</p>
          ) : (
            projectDetail.sections.map((section) => (
              <section key={section.label}>
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-label">{section.label}</h3>
                  <span className="text-sm font-medium tabular-nums text-status-red">
                    {formatDurationHours(section.totalSeconds)}
                  </span>
                </div>
                <EntryTable entries={section.entries} openHoursOnly />
              </section>
            ))
          )}

          <div className="border-t border-separator pt-3 text-sm font-medium text-label">
            Summe offene Stunden:{" "}
            <span className="tabular-nums text-status-red">
              {formatDurationHours(projectDetail.totalSeconds)}
            </span>
          </div>
        </div>
      ) : null}

      {mode === "employee" && employeeDetail ? (
        <>
          <EmployeeStickyActionBar
            totals={employeeDetail.totals}
            selectedCount={selectedEntries.length}
            marking={marking}
            disabled={selectedEntries.length === 0}
            aufNachweisEnabled={aufNachweisEnabled}
            aufNachweisHint={aufNachweisHint}
            onMarkStatus={(status) => void handleMarkStatus(status)}
            onOpenAufNachweis={() => void openTimesheetPicker()}
          />

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
            <p className="text-sm text-label-secondary">
              Ausgewählte Einträge können als Festpreis, Auf Nachweis, Ohne Berechnung oder
              Gewährleistung markiert werden. „Stunden auf Nachweis“ ist nur möglich, wenn alle
              ausgewählten Stunden demselben Projekt angehören – danach wird ein Stundennachweis
              gewählt. Die Markierung überschreibt den bisherigen Status. Bei Festpreis wird der
              Kommentar um <span className="font-medium text-label">FP</span> ergänzt (lokal, falls
              Hero bereits eingereichte Zeiten nicht mehr ändern lässt).
            </p>

            {actionError ? (
              <Alert variant="error" title="Status-Markierung fehlgeschlagen">
                {actionError}
              </Alert>
            ) : null}
            {actionMessage ? (
              <Alert variant="success" title="Status-Markierung">
                {actionMessage}
              </Alert>
            ) : null}

            {employeeDetail.sections.length === 0 ? (
              <p className="text-sm text-label-secondary">Keine Stundeneinträge.</p>
            ) : (
              employeeDetail.sections.map((section, index) => (
                <EmployeeProjectSection
                  key={`${section.label}:${index}`}
                  section={section}
                  sectionKey={`${section.label}:${index}`}
                  selectedUuids={selectedUuids}
                  onToggleUuids={toggleUuids}
                />
              ))
            )}
          </div>
        </>
      ) : null}

      <Dialog
        open={timesheetPickerOpen}
        onClose={() => {
          if (marking) return;
          setTimesheetPickerOpen(false);
        }}
        title="Stundennachweis wählen"
        size="md"
        plainBackdrop
        zIndexClass="z-[60]"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={marking}
              onClick={() => setTimesheetPickerOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={marking || selectedTimesheetId == null || timesheetOptionsLoading}
              loading={marking}
              onClick={() => void handleMarkStatus("auf_nachweis", selectedTimesheetId)}
            >
              Auf Nachweis setzen
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-label-secondary">
            Die ausgewählten Stunden werden in der Stundenpipeline mit dem Status dieses
            Stundennachweises geführt.
          </p>
          {timesheetOptionsLoading ? (
            <div className="flex items-center gap-2 text-sm text-label-secondary">
              <Spinner className="h-4 w-4" />
              Lade Stundennachweise …
            </div>
          ) : null}
          {timesheetOptionsError ? (
            <Alert variant="error" title="Fehler">
              {timesheetOptionsError}
            </Alert>
          ) : null}
          {!timesheetOptionsLoading && !timesheetOptionsError && timesheetOptions.length === 0 ? (
            <Alert variant="warning" title="Keine Nachweise">
              Für dieses Projekt gibt es noch keinen (nicht archivierten) Stundennachweis.
            </Alert>
          ) : null}
          {!timesheetOptionsLoading && timesheetOptions.length > 0 ? (
            <SelectInput
              label="Stundennachweis"
              value={selectedTimesheetId == null ? "" : String(selectedTimesheetId)}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedTimesheetId(value ? Number(value) : null);
              }}
            >
              <option value="">Bitte wählen …</option>
              {timesheetOptions.map((option) => {
                const numberLabel =
                  option.timesheet_number != null && String(option.timesheet_number).trim()
                    ? `#${String(option.timesheet_number).trim()}`
                    : `ID ${option.id}`;
                const range =
                  option.date_from || option.date_to
                    ? ` · ${formatDateDe(option.date_from)} – ${formatDateDe(option.date_to)}`
                    : "";
                return (
                  <option key={option.id} value={option.id}>
                    {numberLabel} · {timesheetStatusLabel(option.status)}
                    {range}
                  </option>
                );
              })}
            </SelectInput>
          ) : null}
        </div>
      </Dialog>
    </Dialog>
  );
}
